import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppAuth } from '@/contexts/AppAuthContext';
import { appValidatePin, formatApiErrorDetail } from '@/lib/appApi';

/**
 * Two-step registration: enter PIN → confirm name + set email/password.
 * Public route — anyone with a valid PIN from an admin can sign up.
 */
export default function AppRegisterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { register } = useAppAuth();

  const [step, setStep] = useState(params.get('pin') ? 'details' : 'pin');
  const [pin, setPin] = useState(params.get('pin') || '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);

  async function onValidatePin(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const meta = await appValidatePin(pin.trim());
      setName(meta.name || '');
      setEmail(meta.email || '');
      setEmailLocked(meta.email_locked);
      setStep('details');
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Invalid code');
    } finally { setBusy(false); }
  }

  async function onComplete(e) {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true); setError('');
    try {
      const u = await register(name.trim(), email.trim(), password, pin.trim());
      toast.success(`Welcome, ${u.display_name || u.name}!`);
      navigate('/', { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Could not create account');
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="app-register-card">
        <CardContent className="pt-8 pb-7 px-7">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <KeyRound className="h-5 w-5 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-semibold text-center text-gray-900">Set up your account</h1>
          <p className="text-center text-sm text-gray-500 mt-1 mb-6">
            {step === 'pin' ? 'Enter the invite code your admin shared with you' : 'Choose your email and password'}
          </p>

          {step === 'pin' && (
            <form onSubmit={onValidatePin} className="space-y-3">
              <Input
                type="text" placeholder="6-digit invite code" autoComplete="one-time-code" required
                value={pin} onChange={(e) => setPin(e.target.value)}
                className="text-center text-lg tracking-widest font-mono"
                data-testid="app-register-pin"
              />
              {error && <p className="text-sm text-destructive text-center" data-testid="app-register-error">{error}</p>}
              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="app-register-pin-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
              </Button>
            </form>
          )}

          {step === 'details' && (
            <form onSubmit={onComplete} className="space-y-3">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required data-testid="app-register-name" />
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email" required autoComplete="email"
                disabled={emailLocked} data-testid="app-register-email"
              />
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (≥ 8 chars)" required autoComplete="new-password" data-testid="app-register-password" defaultVisible />
              <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" required autoComplete="new-password" data-testid="app-register-confirm" defaultVisible />
              {error && <p className="text-sm text-destructive text-center" data-testid="app-register-error">{error}</p>}
              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="app-register-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account & sign in'}
              </Button>
              <button type="button" onClick={() => setStep('pin')} className="block w-full text-xs text-gray-500 hover:underline">
                ← Use a different code
              </button>
            </form>
          )}

          <div className="mt-5 pt-4 border-t border-border/60 text-center">
            <Link to="/portal-signin" className="text-xs text-gray-500 hover:underline">
              Already have an account? Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
